import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import ReportsSidebar, {
  PartiesInvolvedSkeletonLoader,
} from "../../../../../src/features/partiesinvolved/components/PartiesInvolvedSidebar";

import styles from "../ro/components/ROPage.module.css";

import { useAppSelector } from "../../../../../src/app/store";
import { ReportTabListItem } from "../../../../../src/components/ui/tabs/PrimaryReportTabList";
import { cn } from "../../../../../src/lib/utils";

import ButtonV2 from "../../../../../src/components/ui/button/v2";

import { useAppDispatch } from "../../../../../src/app/hooks";
import { RecoveryDetails } from "../../../../api/recovery";
import { SaveButtonV2 } from "../../../../components/ui/button/v2/SaveButton";
import {
  fetchRecoveryDetailsThunk,
  upsertRecoveryDetailsThunk,
} from "../../logic/recoveryThunk";
import {
  ReportDetailsShimmering,
  ShimmerBlock,
} from "../common/ReportDetailsShimmering";
import { LoanRecoveryDetails } from "./RecoveryDetails";
import { isEventsDataLoading } from "../../../../features/timeline/logic/slice";
import { isCaseDocumentsLoading } from "../../../../features/documentManagement/logic/caseDocumentsSlice";
import { getAccountProductInfo } from "../../../../features/creditAccounts/logic/slice";

export const RecoveryPage: React.FC = () => {
  const { id: case_id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const accountProductInfo = useAppSelector(getAccountProductInfo)

  const loanIds = useMemo(
    () => accountProductInfo.map((a) => a.loan_reference_id),
    [accountProductInfo],
  );
  const [activeLoanId, setActiveLoanId] = useState<string>();

  const [discardTrigger, setDiscardTrigger] = useState<number>(0);
  const [dirtyRecoveryDetails, setDirtyRecoveryDetails] = useState<
    RecoveryDetails | undefined
  >(undefined);

  const [isSavingLocal, setIsSavingLocal] = useState(false);
  const [isRecoveryLoading, setIsRecoveryLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const eventsDataLoading = useAppSelector(isEventsDataLoading);
  const documentsLoading = useAppSelector(isCaseDocumentsLoading);

  useEffect(() => {
    setIsPageLoading(isRecoveryLoading || eventsDataLoading || documentsLoading);
  }, [isRecoveryLoading, eventsDataLoading, documentsLoading])

  useEffect(() => {
    if (loanIds.length > 0) {
      setActiveLoanId(loanIds[0]);
    }
  }, [loanIds]);

  useEffect(() => {
    if (!case_id) return;

    let cancelled = false;

    const load = async () => {
      setIsRecoveryLoading(true);
      try {
        await Promise.allSettled([
          dispatch(fetchRecoveryDetailsThunk({ case_id })).unwrap(),
        ]);
      } finally {
        if (!cancelled) setIsRecoveryLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [case_id, dispatch]);

  const handleSaveRecovery = async () => {
    if (!case_id || !activeLoanId) return;
    if (!dirtyRecoveryDetails) return;

    try {
      setIsSavingLocal(true);
      await dispatch(
        upsertRecoveryDetailsThunk({
          case_id,
          request: { recovery_details: [dirtyRecoveryDetails] },
        })
      ).unwrap();
    } finally {
      setIsSavingLocal(false);
    }
  };

  const loanTabItems: ReportTabListItem[] = accountProductInfo.map((account) => ({
    id: account.loan_reference_id,
    tabText: account.ext_account_number || "--",
    pillVariant: "default",
    pillText: "",
    showBadge: false,
  }));

  const renderDiscardButton = () => (
    <ButtonV2
      variant="outline"
      onClick={() => {
        setDiscardTrigger((prev) => prev + 1);
      }}
      disabled={!dirtyRecoveryDetails || isSavingLocal}
      className={cn(
        "font-sans font-medium text-sm leading-5 w-[75.5px] h-8 border-[#3551F3] text-[#3551F3] py-3",
        (!dirtyRecoveryDetails || isSavingLocal) &&
          "opacity-50 cursor-not-allowed"
      )}
    >
      Discard
    </ButtonV2>
  );

  const renderSaveButton = () => (
    <SaveButtonV2
      disabled={!dirtyRecoveryDetails || isSavingLocal}
      onClick={handleSaveRecovery}
      buttonText={isSavingLocal ? "Saving..." : "Save"}
    />
  );

  return (
    <>
      <div
        className={cn(styles.topHeader, "flex items-center justify-end")}
      >
        <div className="flex gap-3">
          {isPageLoading ? (
            <div className="flex gap-3 animate-pulse">
              <ShimmerBlock className="h-8 w-[75.5px]" />
              <ShimmerBlock className="h-8 w-[75.5px]" />
            </div>
          ) : (
            <>
              {renderDiscardButton()}
              {renderSaveButton()}
            </>
          )}
        </div>
      </div>

      <div className={styles.pageContainer}>
        <div className={styles.sidebarWrapper}>
          {isPageLoading ? (
            <PartiesInvolvedSkeletonLoader itemCount={6} />
          ) : (
            <ReportsSidebar
              title="Loan Accounts"
              items={loanTabItems}
              activeSelection={activeLoanId}
              onSelectionChange={setActiveLoanId}
            />
          )}
        </div>

        <div className={styles.mainContent}>
          <div className={styles.contentArea}>
            {isPageLoading ? (
              <ReportDetailsShimmering sectionCount={4} />
            ) : (
              activeLoanId && (
                <LoanRecoveryDetails
                  loanReferenceId={activeLoanId}
                  onChange={setDirtyRecoveryDetails}
                  discardChangesTrigger={discardTrigger}
                />
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RecoveryPage;
